import { Box, Typography, Stack } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import { Helmet } from "react-helmet-async";
import BackButton from "../components/utils/BackButton";

const iconStyle = {
  color: "#f57c00", // orange accent
  fontSize: 22,
  mt: "3px",
};

export default function Disclaimer() {
  return (
    <>
      <Helmet>
        <title>Disclaimer | MarriageBiodata.live</title>
        <meta
          name="description"
          content="Read the Disclaimer for MarriageBiodata.live. Learn about limitations of liability, accuracy of information, and usage responsibility."
        />
        <link
          rel="canonical"
          href="https://www.marriagebiodata.live/disclaimer"
        />
      </Helmet>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://www.marriagebiodata.live",
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Disclaimer",
              "item": "https://www.marriagebiodata.live/disclaimer",
            },
          ],
        })}
      </script>

      <Box sx={{ maxWidth: 1000, mx: "auto", p: { xs: 2, md: 4 } }}>
        <BackButton fallback="/" />
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Disclaimer
        </Typography>

        <Typography paragraph color="text.secondary">
          The information and services provided on MarriageBiodata.live are for
          general informational and personal use only. By using this website,
          you acknowledge and agree to the terms outlined in this Disclaimer.
        </Typography>

        {/* 1. General Information */}
        <Stack direction="row" spacing={1.5} mb={1}>
          <InfoOutlinedIcon sx={iconStyle} />
          <Typography variant="h6" fontWeight={600}>
            1. General Information
          </Typography>
        </Stack>
        <Typography paragraph>
          MarriageBiodata.live is a free online tool that allows users to create
          marriage biodata based on the information they voluntarily enter. We
          do not verify, validate, or guarantee the accuracy, completeness, or
          reliability of any content generated using this tool.
        </Typography>

        {/* 2. User Responsibility */}
        <Stack direction="row" spacing={1.5} mb={1}>
          <WarningAmberOutlinedIcon sx={iconStyle} />
          <Typography variant="h6" fontWeight={600}>
            2. User Responsibility
          </Typography>
        </Stack>
        <Typography paragraph>
          Users are solely responsible for the information they provide and how
          the generated biodata is used or shared. MarriageBiodata.live shall
          not be held responsible for any consequences arising from incorrect,
          misleading, or false information entered by users.
        </Typography>

        {/* 3. No Professional Advice */}
        <Stack direction="row" spacing={1.5} mb={1}>
          <GavelOutlinedIcon sx={iconStyle} />
          <Typography variant="h6" fontWeight={600}>
            3. No Professional or Legal Advice
          </Typography>
        </Stack>
        <Typography paragraph>
          The content generated by this website does not constitute legal,
          matrimonial, financial, or professional advice of any kind. Users are
          advised to exercise their own judgment and discretion before relying
          on any information produced using this tool.
        </Typography>

        {/* 4. Limitation of Liability */}
        <Stack direction="row" spacing={1.5} mb={1}>
          <WarningAmberOutlinedIcon sx={iconStyle} />
          <Typography variant="h6" fontWeight={600}>
            4. Limitation of Liability
          </Typography>
        </Stack>
        <Typography paragraph>
          MarriageBiodata.live is provided on an “as is” and “as available”
          basis. We shall not be liable for any direct, indirect, incidental, or
          consequential damages resulting from the use or inability to use this
          website or its services.
        </Typography>

        {/* 5. External Links */}
        <Stack direction="row" spacing={1.5} mb={1}>
          <LinkOutlinedIcon sx={iconStyle} />
          <Typography variant="h6" fontWeight={600}>
            5. External Links
          </Typography>
        </Stack>
        <Typography paragraph>
          This website may contain links to external websites for reference or
          convenience. We do not have control over the content or practices of
          these third-party sites and are not responsible for any loss or damage
          arising from their use.
        </Typography>

        {/* 6. Updates */}
        <Typography variant="h6" fontWeight={600} gutterBottom>
          6. Changes to This Disclaimer
        </Typography>
        <Typography paragraph>
          We reserve the right to modify or update this Disclaimer at any time
          without prior notice. Continued use of the website after changes are
          made indicates acceptance of the updated Disclaimer.
        </Typography>
      </Box>
    </>
  );
}
